import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, map } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import {
  DevelopmentContextService,
  type DevelopmentContextAction,
  type DevelopmentContextRow,
} from './development-context.service';

const expandedStorageKey = 'momentum.developmentContextHud.expanded';

function readExpandedFromStorage(): boolean {
  try {
    return globalThis.localStorage?.getItem(expandedStorageKey) === '1';
  } catch {
    return false;
  }
}

function writeExpandedToStorage(expanded: boolean): void {
  try {
    globalThis.localStorage?.setItem(expandedStorageKey, expanded ? '1' : '0');
  } catch {
    /* private mode / quota */
  }
}

/** Subsection shown in the dev banner (app shell or feature scope). */
interface DevelopmentHudPanelSection {
  readonly trackId: string;
  readonly title: string;
  readonly rows: DevelopmentContextRow[];
  readonly actions: readonly DevelopmentContextAction[];
}

function actionKey(trackId: string, action: DevelopmentContextAction): string {
  return `${trackId}:${action.label}`;
}

@Component({
  selector: 'app-development-context-hud',
  templateUrl: './development-context-hud.component.html',
  styleUrl: './development-context-hud.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevelopmentContextHudComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly developmentContext = inject(DevelopmentContextService);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly bannerPanelRef = viewChild<ElementRef<HTMLElement>>('bannerPanel');
  private bannerResizeObserver: ResizeObserver | null = null;

  protected readonly enabled = this.developmentContext.isEnabled;

  protected readonly expanded = signal(readExpandedFromStorage());

  /** In-flow spacer height while banner is portaled to `body` above dialog masks. */
  protected readonly bannerHeight = signal(0);

  protected readonly runningActionKey = signal<string | null>(null);
  protected readonly actionMessages = signal<Readonly<Record<string, string>>>({});
  protected readonly actionInputValues = signal<Readonly<Record<string, string>>>({});

  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly panelSections = computed<DevelopmentHudPanelSection[]>(() => {
    const url = this.currentUrl();
    const appRows: DevelopmentContextRow[] = [{ label: 'Route', value: url }];
    const userId = this.authService.userOId();
    if (userId) {
      appRows.push({ label: 'User ID', value: userId });
    }

    const sections: DevelopmentHudPanelSection[] = [
      {
        trackId: 'app',
        title: 'App',
        rows: appRows,
        actions: [],
      },
    ];

    for (const scopeSection of this.developmentContext.displaySections()) {
      sections.push({
        trackId: scopeSection.scopeId,
        title: scopeSection.title,
        rows: scopeSection.rows,
        actions: scopeSection.actions,
      });
    }

    return sections;
  });

  constructor() {
    afterRenderEffect(() => {
      if (!this.enabled() || !this.expanded()) {
        this.unmountBannerPortal();
        return;
      }

      const banner = this.bannerPanelRef()?.nativeElement;
      if (!banner) {
        return;
      }

      this.mountBannerPortal(banner);
      this.observeBannerHeight(banner);
    });

    this.destroyRef.onDestroy(() => this.unmountBannerPortal());
  }

  protected toggleExpanded(): void {
    this.expanded.update((previous) => {
      const next = !previous;
      writeExpandedToStorage(next);
      return next;
    });
  }

  private mountBannerPortal(banner: HTMLElement): void {
    if (banner.parentElement === document.body) {
      return;
    }

    banner.classList.add('dev-context-banner--portal');
    document.body.prepend(banner);
    this.bannerHeight.set(Math.ceil(banner.getBoundingClientRect().height));
  }

  private unmountBannerPortal(): void {
    this.bannerResizeObserver?.disconnect();
    this.bannerResizeObserver = null;
    this.bannerHeight.set(0);

    const banner = this.bannerPanelRef()?.nativeElement;
    if (!banner || banner.parentElement === this.hostRef.nativeElement) {
      return;
    }

    banner.classList.remove('dev-context-banner--portal');
    this.hostRef.nativeElement.prepend(banner);
  }

  private observeBannerHeight(banner: HTMLElement): void {
    if (this.bannerResizeObserver) {
      return;
    }

    this.bannerResizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? 0;
      this.bannerHeight.set(Math.ceil(height));
    });
    this.bannerResizeObserver.observe(banner);
  }

  protected actionMessage(trackId: string, action: DevelopmentContextAction): string | null {
    return this.actionMessages()[actionKey(trackId, action)] ?? null;
  }

  protected isActionRunning(trackId: string, action: DevelopmentContextAction): boolean {
    return this.runningActionKey() === actionKey(trackId, action);
  }

  protected runAction(trackId: string, action: DevelopmentContextAction): void {
    if (this.runningActionKey()) {
      return;
    }

    const key = actionKey(trackId, action);
    const setMessage = (message: string) =>
      this.actionMessages.update((previous) => ({ ...previous, [key]: message }));

    this.runningActionKey.set(key);
    setMessage('Running…');
    const inputValue = action.input ? this.actionInputValue(trackId, action) : undefined;
    action
      .run(inputValue)
      .pipe(
        finalize(() => this.runningActionKey.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (message) => setMessage(message),
        error: () => setMessage('Failed.'),
      });
  }

  protected copyValueToClipboard(value: string): void {
    if (!value || !globalThis.navigator?.clipboard?.writeText) {
      return;
    }
    void globalThis.navigator.clipboard.writeText(value).catch(() => {
      /* ignore — permission denied or unsupported */
    });
  }

  protected actionInputValue(trackId: string, action: DevelopmentContextAction): string {
    const key = actionKey(trackId, action);
    return this.actionInputValues()[key] ?? action.input?.initialValue ?? '';
  }

  protected setActionInputValue(
    trackId: string,
    action: DevelopmentContextAction,
    value: string,
  ): void {
    const key = actionKey(trackId, action);
    this.actionInputValues.update((previous) => ({ ...previous, [key]: value }));
  }
}
